import { useRoute } from 'vue-router'

import { PAGE_ROUTE_NAMES } from '@/constants/route'
import { CategorySortKey, FilterTypes } from '@/constants/filters'

import { usePagination } from '@/composables/usePagination'
import { checkIfRegionCategoriesPage, checkIfRegionSearchPage, checkIfSubCategoryPage } from '@/utils/pageHelpers'

interface Region {
  id: number
  name: string
}

interface RegionSearchNavigationParams {
  fetchList: () => Promise<void>
}

const ITEM_SIZE = 20

export function useRegionSearchNavigation({ fetchList }: RegionSearchNavigationParams) {
  const route = useRoute()
  const localePath = useLocalePath()

  const userPosition = reactive<{ lat: number; lng: number }>({ lat: 0, lng: 0 })
  const hasGeolocation = ref(false)
  const initializedRegion = ref(false)

  const nuxtApp = useNuxtApp()

  const { $store: store } = nuxtApp

  const region = computed(() => store.getters['region/region'])

  const searchKeyword = computed(() => store.getters['search/searchKeyword'])
  const selectedRegion = computed<Region | null>(() => store.getters['search/selectedRegion'])
  const selectedCategoryId = computed<number | null>(() => store.getters['search/selectedCategoryId'])
  const selectedFilters = computed(() => store.getters['filter/filters'])
  const sortType = computed(() => store.getters['filter/sortType'])

  const routerName = computed(() => route.name?.toString() || '')
  const isSearchPage = computed(() => checkIfRegionSearchPage(routerName.value))
  const isCategoriesPage = computed(() => checkIfRegionCategoriesPage(routerName.value))
  const isSubCategoryPage = computed(() => checkIfSubCategoryPage(routerName.value, route.query))

  const sourceCategoryId = computed(() =>
    typeof route.query[FilterTypes.SOURCE_CATEGORY_ID] === 'string' ? Number(route.query[FilterTypes.SOURCE_CATEGORY_ID]) : null
  )
  const categoryId = computed(() => (isSubCategoryPage.value ? sourceCategoryId.value : Number(route.params.id)))

  const { pagingState } = usePagination({ pageSize: ITEM_SIZE })

  watch(
    [searchKeyword, selectedRegion, selectedCategoryId],
    ([newSearchKeyword, newSelectedRegion, newSelectedCategoryId], [oldSearchKeyword, oldSelectedRegion, oldSelectedCategoryId]) => {
      const isRegionChanged = newSelectedRegion?.id !== oldSelectedRegion?.id
      const isCategoryChanged = newSelectedCategoryId !== oldSelectedCategoryId
      const isSearchKeywordChanged = newSearchKeyword !== oldSearchKeyword

      if (initializedRegion.value) {
        initializedRegion.value = false
        return
      }

      if (!isRegionChanged && !isCategoryChanged && !isSearchKeywordChanged) {
        return
      }

      if (isRegionChanged && newSelectedRegion) {
        onRegionChange(newSelectedRegion)
        return
      }

      if (isCategoryChanged && newSelectedCategoryId !== null) {
        onCategoryChange(newSelectedCategoryId)
        return
      }

      // NOTE: 當 sub category tab 觸發搜尋則隱藏 tabs。 refs: https://www.figma.com/design/lWgOu6UGvkU8Wf9Qhs85y3/Sub-Category---APP-Web---FunNow?node-id=754-12548&t=LDhCVqGEM8ZvWejr-4
      if (isSubCategoryPage.value && !!newSearchKeyword) {
        navigateToCategoryPage({ filter_keyword: newSearchKeyword })
        return
      }

      // Preserve existing date/time/headcount filters when only the keyword changes.
      // Without spreading selectedFilters, navigateTo would replace the entire URL
      // query with only {filter_keyword, page}, wiping starttime/endtime/filter_times/limit.
      const newFilter = {
        ...selectedFilters.value,
        filter_keyword: newSearchKeyword || undefined,
        page: 1,
      }
      store.dispatch('filter/setFilters', newFilter)
      navigateTo({ query: newFilter })
      fetchList()
    }
  )

  watch(
    () => pagingState.page,
    newPage => {
      const newQuery = {
        ...selectedFilters.value,
        page: newPage,
        ...(isSubCategoryPage.value ? { [FilterTypes.SOURCE_CATEGORY_ID]: sourceCategoryId.value } : {}),
      }
      navigateTo({ query: newQuery })
      // NOTE：目前 page 是由 usePagination 設定，所以需要加入 page 到 selectedFilters
      updateSelectedFilters({ page: newPage })
      fetchList()
    }
  )

  onMounted(() => {
    getLocation()

    if (selectedRegion.value?.id !== region.value?.id) {
      store.dispatch('search/setSelectedRegion', { id: region.value.id, name: region.value.name })
      initializedRegion.value = true
    }
  })

  function getLocation() {
    if (!navigator.geolocation) {
      return
    }

    navigator.geolocation.getCurrentPosition(position => {
      userPosition.lat = position.coords.latitude
      userPosition.lng = position.coords.longitude
      hasGeolocation.value = !!position.coords.latitude && !!position.coords.longitude
      onGeolocationChange()
    })
  }

  function updateSelectedFilters(newPartialFilters: Record<string, any>) {
    store.dispatch('filter/setFilters', newPartialFilters)
  }

  function onRegionChange(newRegion: Region) {
    const newRegionId = newRegion.id
    store.dispatch('region/getRegion', newRegionId)

    if (isSearchPage.value) {
      navigateTo(localePath({ name: PAGE_ROUTE_NAMES.REGION_SEARCH, params: { rid: newRegionId }, query: {} }))
    } else {
      navigateTo(localePath({ name: PAGE_ROUTE_NAMES.REGION, params: { rid: newRegionId }, query: {} }))
    }
  }

  function onGeolocationChange() {
    if (!hasGeolocation.value || sortType.value !== CategorySortKey.POPULAR) {
      return
    }

    const firstPage = 1
    const currentPage = pagingState.page
    const currentFilter = {
      ...selectedFilters.value,
      ...(isSubCategoryPage.value ? { [FilterTypes.SOURCE_CATEGORY_ID]: sourceCategoryId.value } : {}),
      sortby: CategorySortKey.DISTANCE,
      page: firstPage,
    }

    pagingState.page = firstPage
    updateSelectedFilters(currentFilter)
    navigateTo({ query: currentFilter }, { replace: true })

    // 說明：當頁面更新時， L:82 的 pagingState.page 監聽器會自動觸發 fetchList。
    // 因此這裡需判斷是否已在第一頁，避免重複呼叫 fetchList。
    if (currentPage === firstPage) {
      fetchList()
    }
  }

  function onCategoryChange(newCategoryID: number) {
    const rid = region.value.id

    if (isCategoriesPage.value || isSearchPage.value) {
      navigateTo(localePath({ name: PAGE_ROUTE_NAMES.REGION_CATEGORIES, params: { rid, id: newCategoryID }, query: {} }))
    } else {
      navigateTo(localePath({ name: PAGE_ROUTE_NAMES.REGION_THEMES, params: { rid, id: newCategoryID }, query: {} }))
    }
  }

  function navigateToCategoryPage(query: Record<string, string>) {
    store.dispatch('filter/resetFilterState')
    store.dispatch('setSearchNavigation', false)

    const isValidCategoryId = !Number.isNaN(categoryId.value)

    if (isValidCategoryId) {
      navigateTo(localePath({ name: PAGE_ROUTE_NAMES.REGION_CATEGORIES, params: { rid: region.value.id, id: categoryId.value }, query }))
    } else {
      navigateTo(localePath({ name: PAGE_ROUTE_NAMES.REGION, params: { rid: region.value.id }, query: {} }))
    }
  }

  return {
    userPosition,
    hasGeolocation,
    pagingState,
    onRegionChange,
    onCategoryChange,
  }
}
